import type { AtlasNode, FallbackProfile, AtlasWorkspace } from './types';

export interface ConcentrationRisk {
  node: AtlasNode;
  dependentCount: number;
}

export interface SafetyMargin {
  node: AtlasNode;
  marginMinutes: number;
  state: 'negative' | 'narrow' | 'adequate';
}

export interface FallbackExhaustionRisk {
  node: AtlasNode;
  profile: FallbackProfile;
  gapMinutes: number;
}

export interface WorkspaceOverview {
  ownerCoverage: number;
  recoveryCoverage: number;
  evidenceCoverage: number;
  criticalNodes: AtlasNode[];
  concentrationRisks: ConcentrationRisk[];
  fallbackGaps: AtlasNode[];
  lowConfidenceRelationships: number;
  openFindings: number;
  safetyMargins: SafetyMargin[];
  negativeSafetyMargins: SafetyMargin[];
  missingImpactTolerances: AtlasNode[];
  fallbackProfileCoverage: number;
  fallbackProfileGaps: AtlasNode[];
  fallbackExhaustionRisks: FallbackExhaustionRisk[];
}

function percentage(complete: number, total: number): number {
  return total ? Math.round((complete / total) * 100) : 0;
}

export function summarizeWorkspace(workspace: AtlasWorkspace): WorkspaceOverview {
  const { nodes, edges } = workspace;
  const ownerCoverage = percentage(nodes.filter((node) => node.owner.trim()).length, nodes.length);
  const recoveryCoverage = percentage(nodes.filter((node) => node.estimatedRecoveryMinutes !== undefined).length, nodes.length);
  const evidenceCoverage = percentage(nodes.filter((node) => node.sourceRefs.some((source) => source.trim())).length, nodes.length);
  const criticalNodes = nodes.filter((node) => node.criticality >= 4);

  const dependentCounts = new Map<string, number>();
  for (const edge of edges) {
    if (edge.strength === 'optional' || edge.type === 'FALLS_BACK_TO') continue;
    dependentCounts.set(edge.targetId, (dependentCounts.get(edge.targetId) ?? 0) + 1);
  }
  const concentrationRisks = nodes
    .flatMap((node) => {
      const dependentCount = dependentCounts.get(node.id) ?? 0;
      return node.criticality >= 4 && dependentCount >= 2 ? [{ node, dependentCount }] : [];
    })
    .sort((left, right) => right.dependentCount - left.dependentCount || right.node.criticality - left.node.criticality);

  const fallbackSources = new Set(edges.filter((edge) => edge.type === 'FALLS_BACK_TO').map((edge) => edge.sourceId));
  const fallbackGaps = nodes.filter((node) => node.type === 'capability' && node.criticality >= 4 && !fallbackSources.has(node.id));
  const criticalCapabilities = nodes.filter((node) => node.type === 'capability' && node.criticality >= 4);
  const criticalWithFallback = criticalCapabilities.filter((node) => fallbackSources.has(node.id));
  const profiledCapabilities = new Set(workspace.fallbackProfiles.map((profile) => profile.capabilityId));
  const fallbackProfileGaps = criticalWithFallback.filter((node) => !profiledCapabilities.has(node.id));
  const fallbackExhaustionRisks = workspace.fallbackProfiles.flatMap((profile) => {
    const node = nodes.find((item) => item.id === profile.capabilityId);
    if (!node || node.estimatedRecoveryMinutes === undefined) return [];
    const supportedUntil = profile.activationMinutes + profile.maximumDurationMinutes;
    const gapMinutes = node.estimatedRecoveryMinutes - supportedUntil;
    return gapMinutes > 0 ? [{ node, profile, gapMinutes }] : [];
  }).sort((left, right) => right.gapMinutes - left.gapMinutes);
  const safetyMargins = criticalCapabilities.flatMap((node) => {
    if (node.impactToleranceMinutes === undefined || node.estimatedRecoveryMinutes === undefined) return [];
    const marginMinutes = node.impactToleranceMinutes - node.estimatedRecoveryMinutes;
    return [{ node, marginMinutes, state: marginMinutes < 0 ? 'negative' as const : marginMinutes < 60 ? 'narrow' as const : 'adequate' as const }];
  }).sort((left, right) => left.marginMinutes - right.marginMinutes);

  return {
    ownerCoverage,
    recoveryCoverage,
    evidenceCoverage,
    criticalNodes,
    concentrationRisks,
    fallbackGaps,
    lowConfidenceRelationships: edges.filter((edge) => edge.confidence === 'unknown' || edge.confidence === 'inferred').length,
    openFindings: workspace.findings.filter((finding) => finding.status !== 'closed').length,
    safetyMargins,
    negativeSafetyMargins: safetyMargins.filter((margin) => margin.state === 'negative'),
    missingImpactTolerances: criticalCapabilities.filter((node) => node.impactToleranceMinutes === undefined),
    fallbackProfileCoverage: percentage(criticalWithFallback.length - fallbackProfileGaps.length, criticalWithFallback.length),
    fallbackProfileGaps,
    fallbackExhaustionRisks,
  };
}
